import React from 'react';
import Grid from '@material-ui/core/Grid';
import { Form, useFormikContext } from 'formik';
import flowRight from 'lodash-es/flowRight';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { useRouteHelper } from 'core/hooks/use-route-helper';
import CollapsibleCard from 'core/components/collapsible-card';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import RemoveButton from 'core/components/remove-button';
import { useStore } from 'core/store';
import useStyles from '../person.styles';
import { getCarOptions } from './helper';
import { policyTypes } from '@ourbranch/policy-types';

function LienHolders({
  index,
  fieldName,
  item,
  removeFromList,
  onRemove,
  initialFold,
  onFold,
  disabled,
  openSignalOnError = 0
}) {
  const classes = useStyles();
  const store = useStore();
  const { inPolicyRoute } = useRouteHelper();
  const { values } = useFormikContext();

  const details = inPolicyRoute ? store?.account?.policies?.policy?.segment : values;

  const associatedEntityOptions = () => {
    const showRenters = inPolicyRoute
      ? details?.includeRenters || item?.policyType === policyTypes.Renters
      : !!item.rentersCoverage && !!item.includeRenters;

    if (item.relationship === 'lessor') {
      return getCarOptions({ ...item, ...details }, false);
    }
    return getCarOptions({ ...item, ...details }, showRenters);
  };

  return (
    <CollapsibleCard
      content={
        <>
          <Label
            type="infoSubtitle"
            style={{
              display: 'inline',
              marginRight: 8
            }}
          >
            {`${item.name || ''}`.substring(0, 35)}
            {item.name && item.name.length > 35 && '...'}
          </Label>
          {item.lossPayee && <Label type="infoCardTitle">Loss Payee</Label>}
        </>
      }
      contentRight={
        !item.isPrimary && (
          <RemoveButton disabled={disabled} mode="big" onClick={() => onRemove(index, removeFromList)} />
        )
      }
      initialOpen={initialFold}
      // Open this card when the validation-errors modal navigates to a lienholder error.
      // openSignal is open-only (changed value opens, never force-closes). Mirrors driver.jsx.
      openSignal={openSignalOnError}
      onChange={(val) => onFold(index, val)}
    >
      <Form disabled={disabled} className={classes.container}>
        <Grid key="container" container justifyContent="space-around" alignItems="center" spacing={4}>
          <FormField
            mode="light"
            name={`${fieldName}.name`}
            id={`${fieldName}.name`}
            label="Name or Company"
            type="string"
            xs={item.relationship === 'lienholder' ? 8 : 12}
            permissions={{ isLicensedAction: false }}
          />
          {item.relationship === 'lienholder' && (
            <FormField
              mode="light"
              name={`${fieldName}.loanNumber`}
              id={`${fieldName}.loanNumber`}
              label="Loan Number (optional)"
              type="string"
              optional
              xs={4}
              permissions={{ isLicensedAction: false }}
            />
          )}
          <FormField
            mode="light"
            name={`${fieldName}.address.address`}
            id={`${fieldName}.address.address`}
            label="Address"
            type="address"
            xs={8}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.address2`}
            id={`${fieldName}.address.address2`}
            label="Address 2"
            type="string"
            optional
            xs={4}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.city`}
            id={`${fieldName}.address.city`}
            label="City"
            type="string"
            optional
            xs={4}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.state`}
            id={`${fieldName}.address.state`}
            label="State"
            type="state"
            xs={4}
            optional
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.zip`}
            id={`${fieldName}.address.zip`}
            label="Zip code"
            type="string"
            optional
            xs={4}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.VIN`}
            id={`${fieldName}.VIN`}
            label={
              item.relationship === 'lessor' ? 'Selected Associated Entity' : 'Selected Associated Entity (optional)'
            }
            type="select"
            options={associatedEntityOptions()}
            optional
            fast={false}
            xs={8}
            permissions={{ isLicensedAction: false }}
          />
        </Grid>
      </Form>
    </CollapsibleCard>
  );
}

LienHolders.propTypes = {
  index: PropTypes.number.isRequired,
  fieldName: PropTypes.string.isRequired,
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    isPrimary: PropTypes.bool,
    address: PropTypes.shape({
      address: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string
    }),
    loanNumber: PropTypes.string,
    VIN: PropTypes.string
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  removeFromList: PropTypes.func.isRequired,
  initialFold: PropTypes.bool.isRequired,
  onFold: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  // Bumped by form-list when this item is the validation-errors-modal scroll target, to open its card.
  openSignalOnError: PropTypes.number
};

LienHolders.defaultProps = {
  disabled: false,
  openSignalOnError: 0
};

export default flowRight(observer)(LienHolders);
